import { BrandKey } from "@cerberus/branding";
import { ModuleId } from "@cerberus/modules";

/**
 * Shape of `window.runConfig`, injected at deploy time by `public/runtime-config.js`
 * (loaded before the React bundle). This is the single, typed accessor for those
 * values — prefer it over reading `window.runConfig` directly.
 */
export interface RuntimeConfig {
  baseApiUrl: string;
  logoutReturnToUrl?: string;
  /** Active brand for this deployment; defaults to TeleHeadache when unset. */
  brand?: BrandKey;
  /**
   * Explicit list of enabled modules for this deployment. When omitted, the
   * brand's default module set is used (see `@cerberus/modules`).
   */
  modules?: ModuleId[];
  /**
   * Questionnaire definition id used by the external-provider "Refer a patient"
   * flow. Optional — falls back to the seeded default when unset.
   */
  referralQuestionnaireId?: string;
  /**
   * Override for the questionnaire the Welcome Survey hands a new patient off to. Optional —
   * defaults to the TeleHeadache questionnaire, whose id is a code constant on the API and the
   * same on every cell (`TH_QUESTIONNAIRE_ID`).
   */
  welcomeSurveyQuestionnaireId?: string;
  /**
   * How many minutes before a video visit a patient may enter the pre-join lobby
   * (device test + countdown). Optional — defaults to 5 when unset. See `VideoLobby`.
   */
  videoLobbyWindowMinutes?: number;
  /**
   * Pre-launch: patient self-signup is closed and `/i/new-patient` collects an email address for
   * the launch announcement instead (`LaunchWaitlist`). Must match `signupBlocked` in
   * `deploy/cells.ts`, which also closes the API side (`MTC:Signup:Blocked`) — `checkFrontendConfigs`
   * refuses a deploy where they disagree. th-prod and th-staging, 2026-10-07.
   */
  signupBlocked?: boolean;
}

export function getRunConfig(): RuntimeConfig {
  return (window as unknown as { runConfig: RuntimeConfig }).runConfig;
}

/** See {@link RuntimeConfig.signupBlocked}. */
export function isSignupBlocked(): boolean {
  return getRunConfig()?.signupBlocked === true;
}
